"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { DEFAULT_ROOM } from "@/lib/chat/rooms";
import type { ChatMessage } from "@/lib/chat/types";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { Icon } from "@/components/ui/Icon";
import { ChatRow } from "./ChatPanel";
import { useShell } from "./ShellContext";

const POLL_MS = 10_000;
const LIMIT = 40;

/* The chat as a signed-out visitor sees it (Feature flags → Visitors see the
   chat): the room's recent messages, read-only, and the way in where the
   composer would be. It polls the room's history over HTTP instead of
   opening the realtime connection a signed-in player's panel holds — a
   visitor costs one small request every few seconds, and none while the
   tab is hidden. */
export function GuestChatPanel({ onClose, side = "right" }: { onClose: () => void; side?: "left" | "right" }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const { openSignIn } = useShell();
  const { t } = useI18n();
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (document.visibilityState === "hidden") return;
      const response = await fetch(`/api/chat/rooms/${DEFAULT_ROOM}/messages?limit=${LIMIT}`, { cache: "no-store" }).catch(() => null);
      if (!response?.ok || cancelled) return;
      const payload = await response.json().catch(() => null);
      const history = (payload?.data?.messages ?? []) as ChatMessage[];
      if (!cancelled) setMessages(history.filter((message) => !message.deletedAt));
    };

    void load();
    const timer = setInterval(load, POLL_MS);
    document.addEventListener("visibilitychange", load);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", load);
    };
  }, []);

  useEffect(() => {
    const node = listRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages.length]);

  return (
    <aside
      className={cn(
        "flex h-full w-full shrink-0 flex-col bg-elevation-1",
        side === "left" ? "border-r border-border" : "border-l border-border",
      )}
    >
      <div className="flex h-[56px] shrink-0 items-center gap-[8px] border-b border-border px-[16px]">
        <span className="h-[8px] w-[8px] rounded-full bg-success-fg" aria-hidden />
        <p className="flex-1 text-[13px] font-bold leading-[1.6] text-fg">{t("topbar.chat")}</p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close chat"
          className="grid h-[32px] w-[32px] place-items-center rounded-lg bg-elevation-2 text-fg-subtle transition-colors hover:bg-elevation-3 hover:text-fg"
        >
          <Icon name="close" size={16} />
        </button>
      </div>

      <div ref={listRef} className="scrollbar-none min-h-0 flex-1 overflow-y-auto py-[8px]">
        {messages.length === 0 ? (
          <p className="px-[16px] py-[24px] text-center text-[12px] leading-[1.6] text-fg-subtle">It is quiet in here. Say hi.</p>
        ) : (
          messages.map((message) => <ChatRow key={message.id} message={message} onRetry={() => undefined} />)
        )}
      </div>

      <div className="shrink-0 border-t border-border p-[12px]">
        <button
          type="button"
          onClick={() => openSignIn()}
          className="flex h-[46px] w-full items-center justify-between rounded-md border border-border-input bg-elevation-3 pl-[12px] pr-[8px] text-[12px] font-normal leading-[1.6] text-fg-subtle transition-colors hover:text-fg"
        >
          {t("chat.signInToChat")}
          <span className="grid h-[30px] w-[30px] place-items-center rounded-sm bg-brand text-fg-on-primary">
            <Icon name="send" size={16} />
          </span>
        </button>
      </div>
    </aside>
  );
}
